// Variations
//
if (!customElements.get("variant-selects")) {
  class VariantSelects extends HTMLElement {
    constructor() {
      super();

      this.initializeProductSwapUtility();

      this.addEventListener("change", this.handleProductUpdate);

      this.sectionId = this.dataset.section;
      this.hideOutOfStock = this.dataset.variantsVisibility === "hide";
      this.hideSingle = this.dataset.hideSingle === "true";
      this.preselectVariant = this.dataset.preselectVariant === "true";
      this.optionsWithValues = JSON.parse(
        this.querySelector('[type="application/json"][data-id="product-options-with-values"]')
          .textContent,
      );
      this.variants = JSON.parse(
        this.querySelector('[type="application/json"][data-id="product-variants"]')?.textContent ??
          null,
      );

      this.updateVisibility();
      this.initializeSelectedVariantFromUrl();
    }

    initializeSelectedVariantFromUrl() {
      const urlVariantId = new URLSearchParams(window.location.search).get("variant");
      const checkedInput = this.querySelector("fieldset input:checked, select option:checked");
      const urlVariant = urlVariantId ? this.variants?.find((variant) => String(variant.id) === String(urlVariantId)) : null;
      const candidateInputs = Array.from(this.querySelectorAll("fieldset input, select option"));
      const selectedInput = urlVariant
        ? candidateInputs.find((input) => String(this.getVariantData(input.id)?.id) === String(urlVariant.id)) || checkedInput
        : checkedInput;
      const selectedVariant = urlVariant || (selectedInput?.id ? this.getVariantData(selectedInput.id) : null);
      if (!selectedVariant) return;
      if (selectedInput?.tagName === "OPTION") selectedInput.selected = true;
      else if (selectedInput) selectedInput.checked = true;
      this.currentVariant = selectedVariant;
      this.updateMedia();
      this.updateVariantInput();
      this.toggleAddButton(!this.currentVariant.available);
      const targetUrl = selectedInput?.dataset.productUrl || this.dataset.url;
      const productUrl = this.getProductInfoUrl(targetUrl, this.currentVariant.id);
      this.renderProductInfo(productUrl, selectedInput?.id || `variant-${this.currentVariant.id}`, this.handleUpdateProductInfo(this.sectionId).bind(this));
    }

    initializeProductSwapUtility() {
      this.swapProductUtility = new HTMLUpdateUtility();
      this.swapProductUtility.addPostProcessCallback((newNode) => {
        window?.Shopify?.PaymentButton?.init();
        window?.ProductModel?.loadShopifyXR();
        publish(PUB_SUB_EVENTS.sectionRefreshed, {
          data: {
            sectionId: this.sectionId,
            resource: {
              type: "product",
              id: newNode.querySelector("variant-selects, variant-radios").dataset.productId,
            },
          },
        });
      });
    }

    handleProductUpdate(event) {
      const input = this.getInputForEventTarget(event.target);
      const targetId = input.id;
      const targetUrl = input.dataset.productUrl || this.dataset.url;
      const sectionId = this.sectionId;

      this.currentVariant = this.getVariantData(targetId);
      this.currentVariant = this.maybeGetFirstAvailableVariant(this.currentVariant, input);

      this.hideVariants = this.dataset.hideVariants === "true";

      let productUrl = this.getProductInfoUrl(targetUrl, this.currentVariant?.id);

      // Short circuit the rendering logic
      // when we are in the quick view drawer
      const quickViewDrawer = this.closest("quick-view-drawer");
      if (quickViewDrawer) {
        this.handleQuickviewUpdate(productUrl, targetId);
        return;
      }

      this.toggleAddButton(true, "");

      let callback = () => {};

      if (this.dataset.url !== targetUrl || (this.hideVariants && this.currentVariant)) {
        // Combined listing - Different product (or hide variants is enabled)
        this.updateURL(targetUrl);
        this.updateShareUrl(targetUrl);
        callback = this.handleSwapProduct(sectionId);

        // Make sure we fetch only the section if we're in a featured product
        const shouldFetchFullPage = this.dataset.updateUrl === "true";
        productUrl = this.getProductInfoUrl(targetUrl, this.currentVariant?.id, shouldFetchFullPage);
      } else if (!this.currentVariant) {
        // Variant does not exist.
        this.setUnavailable();
        callback = (html) => {
          this.updatePickupAvailability();
          this.updateOptionValues(html);
        };
      } else {
        // Product variant.
        this.updateMedia();
        this.updateURL(targetUrl);
        this.updateShareUrl(targetUrl);
        this.updateVariantInput();
        callback = this.handleUpdateProductInfo(sectionId).bind(this);
      }

      this.renderProductInfo(productUrl, targetId, callback);
    }

    maybeGetFirstAvailableVariant(currentVariant, inputClicked) {
      /* Handle case where immediate selection of the first available variant is required.
       * hideOutOfStock needs to be enabled and the following line is required under (inside) <variant-selects> and <variant-radios>
       * in snippets/product-variant-picker.liquid:
       *
       * <script type="application/json" data-id="product-variants">{{ product.variants | json }}</script>
       */
      let newVariant = currentVariant;

      if (this.variants && this.hideOutOfStock && (!currentVariant || !currentVariant.available)) {
        const selectedOptionPosition =
          inputClicked.closest("fieldset, select").dataset.optionPosition;

        const selectedOptions = Array.from(
          this.querySelectorAll("fieldset input:checked, select option:checked"),
        ).reduce((acc, element) => {
          acc.push(element.value);
          return acc;
        }, []);
        const firstAvailableVariant = this.variants.find((variant, index) => {
          // Subset the arrays up to the selected option's index, so that the next available variant
          // has common values with all options above (and including) the clicked one.
          // Note: selectedOptionPosition is 1-based but slice() does not include the last element, so no arithmetic is needed.
          const variantSlice = variant.options.slice(0, selectedOptionPosition).toString(),
            selectedSlice = selectedOptions.slice(0, selectedOptionPosition).toString();

          return variant.available && variantSlice === selectedSlice;
        });

        if (firstAvailableVariant) {
          newVariant = firstAvailableVariant;
        }
      }

      return newVariant;
    }

    getInputForEventTarget(target) {
      return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
    }

    getWrappingSection(sectionId) {
      return (
        this.closest(`#shopify-section-${sectionId}`) || // should match both main-product and featured-product
        null
      );
    }

    handleSwapProduct(sectionId) {
      return (html) => {
        const oldContent = this.getWrappingSection(sectionId);
        if (!oldContent) {
          return;
        }

        document.getElementById(`ProductModal-${sectionId}`)?.remove();

        const response = html.getElementById(`shopify-section-${sectionId}`);

        if (this.dataset.updateUrl === "true") {
          this.swapProductUtility.viewTransition(
            document.querySelector("main"),
            html.querySelector("main"),
          );
          document.querySelector("head title").innerHTML = html.querySelector("head title").innerHTML;
        } else {
          this.swapProductUtility.viewTransition(oldContent, response);
        }
      };
    }

    /**
     * Handles updating a variant in the Quickview drawer.
     */
    handleQuickviewUpdate(productUrl, targetId) {
      const quickViewDrawer = this.closest("quick-view-drawer");
      const productForm = quickViewDrawer.querySelector("product-form form");

      if (productForm) {
        productForm.style.opacity = "0.7";
      }

      this.renderProductInfo(productUrl, targetId, (html) => {
        quickViewDrawer.renderProduct(html, this.currentVariant);
      });
    }

    updateMedia() {
      const mediaGallery = document.getElementById(`MediaGallery-${this.dataset.section}`);

      if (!this.currentVariant || !this.currentVariant.featured_media) {
        return;
      }

      if (mediaGallery?.setActiveMedia) {
        mediaGallery.setActiveMedia(
          this.currentVariant.featured_media.id,
          mediaGallery.getAttribute("hide-variants") === "true",
        );
      }
    }

    updateURL(url) {
      if (!this.currentVariant || this.dataset.updateUrl === "false") {
        return;
      }
      window.history.replaceState(
        {},
        "",
        `${url}${this.currentVariant?.id ? `?variant=${this.currentVariant.id}` : ""}`,
      );
    }

    updateShareUrl(url) {
      const shareButtons = document.querySelectorAll(
        `Product-${this.dataset.section} .list-social-item a`,
      );
      if (!shareButtons) {
        return;
      }
      shareButtons.forEach((button) => {
        const fullUrl = `${window.shopUrl}${url}${
          this.currentVariant?.id ? `?variant=${this.currentVariant.id}` : ""
        }`;
        button.href = `${button.dataset.shareUrl}${encodeURIComponent(fullUrl)}`;
      });
    }

    updateVariantInput() {
      const productForms = document.querySelectorAll(
        `#product-form-${this.dataset.section}, #product-form-installment`,
      );

      productForms.forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }

    updatePickupAvailability() {
      const pickUpAvailability = document.querySelector("pickup-availability");

      if (!pickUpAvailability || !pickUpAvailability.fetchAvailability) {
        return;
      }

      if (this.currentVariant && this.currentVariant.available) {
        pickUpAvailability.fetchAvailability(this.currentVariant.id);
      } else {
        pickUpAvailability.removeAttribute("available");
        pickUpAvailability.innerHTML = "";
      }
    }

    updateVisibility() {
      // Check if there's only one variant *defined*, i.e. each attribute/option has only one possible value.
      const hasSingleVariant = Array.from(
        this.querySelectorAll("variant-radios fieldset, variant-selects select"),
      ).reduce((acc, fieldset) => {
        return acc && fieldset.querySelectorAll("input, option").length === 1;
      }, true);

      const parent = this.closest(".product-variants");

      if (this.hideSingle && hasSingleVariant && this.preselectVariant) {
        parent?.classList.add("hidden");
      } else {
        parent?.classList.add("variants-visible");
        parent?.classList.remove("variants-hidden");
      }
    }

    updateOptionValues(html) {
      const variantSelects = html.querySelector("variant-selects, variant-radios");
      if (variantSelects) {
        this.innerHTML = variantSelects.innerHTML;
      }
    }

    getProductInfoUrl(url, variantId, shouldFetchFullPage = false) {
      const params = [];

      if (!shouldFetchFullPage) {
        params.push(`section_id=${this.sectionId}`);
      }

      if (variantId) {
        params.push(`variant=${variantId}`);
      } else {
        const optionValues = this.getSelectedOptionValues();
        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }
      }

      return `${url}?${params.join("&")}`;
    }

    renderProductInfo(productUrl, targetId, callback) {
      this.abortController?.abort();
      this.abortController = new AbortController();

      fetch(productUrl, {
        signal: this.abortController.signal,
      })
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, "text/html");
          callback(html);
        })
        .then(() => {
          // set focus to last clicked option value
          const target = document.getElementById(targetId);
          target?.focus();
          target?.tagName === "OPTION" ? (target.selected = true) : false;
        });
    }

    handleUpdateProductInfo(sectionId) {
      return (html) => {
        // Get the product-form custom element wrapper, not just the inner form.
        // This ensures event listeners are properly reattached when the element is replaced.
        const productFormSource = html
          .getElementById(`product-form-${sectionId}`)
          ?.closest("product-form");
        const productFormDestination = document
          .getElementById(`product-form-${sectionId}`)
          ?.closest("product-form");

        const infoMetas = document.querySelectorAll(`.product-info-meta-${this.dataset.section}`);

        if (infoMetas.length > 0) {
          infoMetas.forEach((infoMeta) => {
            infoMeta.classList.remove("visibility-hidden");
          });
        }

        if (productFormSource && productFormDestination) {
          productFormDestination.outerHTML = productFormSource.outerHTML;
        }

        const blockDestinations = document.querySelectorAll(
          `.product-block-content-${sectionId}.variant-changed-update`,
        );
        const blockSources = html.querySelectorAll(
          `.product-block-content-${sectionId}.variant-changed-update`,
        );
        blockDestinations.forEach((blockDestination, index) => {
          const blockSource = blockSources[index];
          if (blockSource) {
            blockDestination.outerHTML = blockSource.outerHTML;
          }
        });

        this.toggleAddButton(!this.currentVariant.available);
        this.setSku(this.currentVariant.sku);
        this.setBarcode(this.currentVariant.barcode);

        this.setProductInfo(html);
        this.updatePickupAvailability();
        this.updateOptionValues(html);

        const event = new CustomEvent("product:variant-change", {
          detail: {
            variant: this.currentVariant,
          },
        });
        document.dispatchEvent(event);

        publish(PUB_SUB_EVENTS.productVariantChange, {
          sectionId: sectionId,
          html,
          variant: this.currentVariant,
        });
      };
    }

    setSku(sku) {
      const skuFields = document.querySelectorAll(`.ProductSku-${this.dataset.section}`);

      if (!skuFields.length) {
        return;
      }

      if (!sku) {
        skuFields.forEach((field) => {
          field.parentNode.classList.add("visually-hidden");
        });
        return;
      }
      skuFields.forEach((field) => {
        field.parentNode.classList.remove("visually-hidden");
        field.textContent = sku;
      });
    }

    setBarcode(barcode) {
      const barcodeFields = document.querySelectorAll(`.ProductBarcode-${this.dataset.section}`);

      if (!barcodeFields.length) {
        return;
      }

      if (!barcode) {
        barcodeFields.forEach((field) => {
          field.parentNode.classList.add("visually-hidden");
        });
        return;
      }

      barcodeFields.forEach((field) => {
        field.parentNode.classList.remove("visually-hidden");
        field.textContent = barcode ?? "";
      });
    }

    setProductInfo(html) {
      const className = `#ProductPage-${this.sectionId} .product-info-details-list`;
      const destinations = document.querySelectorAll(className);
      const sources = html.querySelectorAll(className);

      if (destinations.length > 0) {
        destinations.forEach((destination, index) => {
          if (sources[index]) {
            destination.innerHTML = sources[index]?.innerHTML;
          }
        });
      }
    }

    toggleAddButton(disable = true) {
      const productForm = document.getElementById(`product-form-${this.dataset.section}`);
      if (!productForm) {
        return;
      }

      const addButton = productForm.querySelector('[name="add"]');

      if (!addButton) {
        return;
      }

      if (disable) {
        addButton.setAttribute("disabled", "disabled");
      } else {
        addButton.removeAttribute("disabled");
      }
    }

    setUnavailable() {
      const form = document.getElementById(`product-form-${this.dataset.section}`);
      const addButton = form?.querySelector('[name="add"]');
      const addButtonText = form?.querySelector('[name="add"] > span');
      const price = document.getElementById(`price-${this.dataset.section}`);
      const product = document.getElementById(`ProductPage-${this.dataset.section}`);
      const infoMetas = document.querySelectorAll(`.product-info-meta-${this.dataset.section}`);
      const stockBarBlock = document.getElementById(`ProductStockBar-${this.dataset.section}`);

      // If a variant was not preselected, the form is hidden. Make sure to show it so that the user knows what's happening.
      if (!this.preselectVariant) {
        form.closest(".product-form")?.classList.remove("visually-hidden");
      }

      if (addButton) {
        addButtonText.textContent = 'Vælg variant';
      }

      if (price) {
        price.classList.add("visibility-hidden");
      }

      if (infoMetas.length > 0) {
        infoMetas.forEach((infoMeta) => {
          infoMeta.classList.add("visibility-hidden");
        });
      }

      if (stockBarBlock) {
        stockBarBlock.classList.add("hidden");
      }

      // SKU and Barcode are variant-depended, so we need to "empty" them but not remove them (so the Details block container won't end up empty).
      const unavailableDetails = product?.querySelectorAll(
        ".product-info-details-variant-field .product-info-details-item-value",
      );
      if (unavailableDetails) {
        unavailableDetails.forEach((detail) => (detail.innerHTML = "-"));
      }
    }

    getVariantData(inputId) {
      return JSON.parse(
        this.querySelector(`script[type="application/json"][data-resource="${inputId}"]`).textContent,
      );
    }

    getSelectedOptionValues() {
      return Array.from(this.querySelectorAll("select option:checked, fieldset input:checked")).map(
        (element) => element.dataset.optionValueId,
      );
    }
  }

  customElements.define("variant-selects", VariantSelects);

  class VariantRadios extends VariantSelects {
    constructor() {
      super();
    }
  }

  customElements.define("variant-radios", VariantRadios);
}
